import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Horizontally scrollable row that shows a chevron at each end only when there
// is more content to scroll in that direction. Clicking a chevron scrolls.
const ScrollableTabs = ({ children, className = '' }) => {
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 1);
    setCanRight(Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [update]);

  // Recompute after every render so added/removed tabs or loaded count badges
  // toggle the chevrons correctly.
  useEffect(() => {
    update();
  });

  const scrollByDir = (dir) => {
    const el = scrollRef.current;
    if (el)
      el.scrollBy({ left: dir * el.clientWidth * 0.6, behavior: 'smooth' });
  };

  return (
    <div className="relative">
      {canLeft && (
        <button
          type="button"
          onClick={() => scrollByDir(-1)}
          aria-label="Scroll left"
          className="absolute left-0 top-0 bottom-0 z-10 flex items-center pl-1 pr-4 bg-gradient-to-r from-muted via-muted to-transparent text-muted-foreground hover:text-foreground-strong"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}
      <div
        ref={scrollRef}
        className={`flex overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${className}`}
      >
        {children}
      </div>
      {canRight && (
        <button
          type="button"
          onClick={() => scrollByDir(1)}
          aria-label="Scroll right"
          className="absolute right-0 top-0 bottom-0 z-10 flex items-center pr-1 pl-4 bg-gradient-to-l from-muted via-muted to-transparent text-muted-foreground hover:text-foreground-strong"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

const FilterButton = ({
  filters,
  onFilterChange,
  defaultActiveFilter,
  isSubFilter = false,
  highlightedValue = null,
  countStats,
}) => {
  const [activeFilter, setActiveFilter] = useState(
    defaultActiveFilter || filters[0]?.value
  );

  useEffect(() => {
    if (defaultActiveFilter) {
      setActiveFilter(defaultActiveFilter);
    }
  }, [defaultActiveFilter]);

  const handleTabChange = (value) => {
    setActiveFilter(value);
    if (onFilterChange) {
      onFilterChange(value);
    }
  };

  const countBadge = (filter, isActive) => {
    if (
      typeof countStats === 'object' &&
      filter.statsLabel &&
      countStats[filter.statsLabel] > 0
    ) {
      return (
        <span
          className={`text-[11px] font-semibold px-[7px] py-px rounded-[10px] leading-[1.5] ${
            isActive
              ? 'bg-brand-bg-subtle text-primary'
              : 'bg-border text-muted-foreground'
          }`}
        >
          {countStats[filter.statsLabel]}
        </span>
      );
    }
    return null;
  };

  if (isSubFilter) {
    return (
      <div className="w-full">
        <ScrollableTabs className="border-b border-border">
          {filters.map((filter) => {
            const isActive = activeFilter === filter.value;
            return (
              <button
                key={filter.value}
                type="button"
                onClick={() => onFilterChange && onFilterChange(filter.value)}
                className={`inline-flex items-center gap-2 px-4 py-2 text-[13px] border-b-2 -mb-px whitespace-nowrap cursor-pointer transition-colors duration-150 ${
                  isActive
                    ? 'border-primary text-primary font-semibold'
                    : 'border-transparent text-muted-foreground font-medium hover:text-muted-foreground'
                }`}
                data-state={isActive ? 'active' : 'inactive'}
                aria-pressed={isActive}
              >
                {filter.label}
                {countBadge(filter, isActive)}
              </button>
            );
          })}
        </ScrollableTabs>
      </div>
    );
  }

  return (
    <div className="w-full">
      <ScrollableTabs>
        {filters.map((filter) => {
          const isActive =
            activeFilter === filter.value || highlightedValue === filter.value;
          return (
            <button
              key={filter.value}
              type="button"
              onClick={() => handleTabChange(filter.value)}
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-[13px] border-b-2 -mb-px whitespace-nowrap cursor-pointer transition-colors duration-150 ${
                isActive
                  ? 'border-primary text-primary font-semibold'
                  : 'border-transparent text-muted-foreground font-medium hover:text-muted-foreground'
              }`}
              data-state={isActive ? 'active' : 'inactive'}
              aria-pressed={isActive}
            >
              {filter.icon && (
                <span className="flex items-center">{filter.icon}</span>
              )}
              <span className="truncate">{filter.label}</span>
              {countBadge(filter, isActive)}
            </button>
          );
        })}
      </ScrollableTabs>
    </div>
  );
};

export default FilterButton;
